<script>
import { format } from 'date-fns';
import { DATE_RANGE_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import SummaryIndicator from '../../displayComponents/Summary.vue';
import DonutChart from './Donut.vue';
import ColumnChart from './ColumnChart.vue';
import TableIndicator from './Table.vue';
import metricComponentsMixin from '../../mixins/metricComponentsMixin.js';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    SummaryIndicator,
    DonutChart,
    ColumnChart,
    TableIndicator,
    InboxName,
  },
  mixins: [metricComponentsMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    display: {
      type: Object,
      required: true,
    },
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    dateRange: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    widgetVisualizationOptions: {
      type: Object,
      default: () => ({}),
    },
    submetric: {
      type: Object,
      default: () => ({}),
    },
    multipleSubmetrics: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const translatedDateRangeOptions = this.translatedDateRangeOptions();

    return {
      selectedDateRange: translatedDateRangeOptions[0], // Intervalo de data selecionado inicialmente
      options: translatedDateRangeOptions, // Opções de intervalo de data disponíveis
      selectedSubmetric:
        this.translatedConversationResolutionReasonsSubmetricsOptions()[0],

      selectedMultipleSubmetrics: [
        this.translatedConversationResolutionReasonsSubmetricsOptions()[0],
      ],

      editingWidget: false, // Indica se o widget está em modo de edição
      isLoading: true, // Estado de carregamento
      errorInReq: false, // Estado de erro de requisição
      summaryData: {}, // Dados de resumo carregados
      chartData: {}, // Dados para o gráfico de colunas
      shouldShowPreview: false, // Indica se a pré-visualização deve ser exibida
      componentWidth: 0, // Largura atual do componente
    };
  },

  computed: {
    // Verifica se o usuário está na página de edição ou criação do widget
    canEdit() {
      const onEditPage = this.$route.path.endsWith('/edit');
      const onCreationPage =
        this.$route.path.endsWith('/custom_dashboards') &&
        !this.$route.params?.dashboardId;
      return onEditPage || onCreationPage;
    },
    // Conteúdo a ser exibido no resumo
    summaryContent() {
      if (this.submetric && this.submetric.type && this.submetric.type !== '') {
        const totalResolutionsForSubmetric =
          this.summaryData.inboxes_summary.reduce((sum, inbox) => {
            return sum + inbox.total_resolutions;
          }, 0);
        return totalResolutionsForSubmetric?.toLocaleString() || 0;
      }
      return this.summaryData.total_resolutions?.toLocaleString() || 0;
    },
    // Descrição do intervalo de datas selecionado
    dateRangeDescription() {
      if (this.selectedDateRange.id === 'CUSTOM_DATE_RANGE') {
        const formatUnixToDate = unixTimestamp =>
          new Date(unixTimestamp * 1000).toLocaleDateString('en-CA');
        const [from, to] = this.dateRange.range;
        return `${formatUnixToDate(from)}...${formatUnixToDate(to)}`;
      }
      return this.$t(this.selectedDateRange.translationKey);
    },
    // Obtém o nome da caixa de entrada atual a partir do store
    currentInbox() {
      return this.$store.getters['inboxes/getInbox'](this.inbox.id);
    },
    // Define a altura do conteúdo do widget dinamicamente
    widgetContentHeightClass() {
      const { metric, dateRange, inbox } = this.widgetVisualizationOptions;
      const showingDescriptions = Boolean(
        metric || dateRange || (inbox && this.inbox?.id)
      );
      return showingDescriptions ? 'h-[calc(100%-40px)]' : 'h-full';
    },
    // Verifica se há mais de uma submétrica selecionada
    checksIfSeveralSubmetricsAreSelected() {
      return this.multipleSubmetrics.length > 1;
    },
  },

  methods: {
    // Tradução das opções de intervalo de datas
    translatedDateRangeOptions() {
      return Object.values(DATE_RANGE_OPTIONS).map(option => ({
        ...option,
        name: this.$t(option.translationKey),
      }));
    },

    // Define a data inicial com base no intervalo de datas selecionado
    setInitialData() {
      const { type: dateRangeType } = this.dateRange;
      this.selectedDateRange = this.translatedDateRangeOptions().find(
        e => e.id === dateRangeType
      );

      if (this.submetric) {
        const { type: submetricType } = this.submetric;
        this.selectedSubmetric =
          this.translatedConversationResolutionReasonsSubmetricsOptions().find(
            e => e.type === submetricType
          );
      }

      if (this.multipleSubmetrics) {
        this.selectedMultipleSubmetrics = [];
        this.multipleSubmetrics.forEach(item => {
          const { type: submetricType } = item;
          this.selectedMultipleSubmetrics.push(
            this.translatedConversationResolutionReasonsSubmetricsOptions().find(
              e => e.type === submetricType
            )
          );
        });
      }
    },

    // Abre o modal de edição do widget
    openEditModal() {
      emitter.emit('openEditWidgetModal', { id: this.id });
    },

    // Busca dados para o gráfico de barras com base no intervalo de datas
    async fetchBarData() {
      if (!this.dateRange || !this.dateRange.range) {
        return;
      }

      try {
        const [from, to] = this.dateRange.range;

        const totalDays = (to - from) / (24 * 60 * 60);
        let intervals;

        if (totalDays <= 7) {
          // Intervalo de 7 dias ou menos
          intervals = this.getDaysInRange(from, to);
        } else if (totalDays <= 90) {
          // Últimos 3 meses
          intervals = this.getMonthsInRange(
            new Date(from * 1000),
            new Date(to * 1000)
          );
        } else if (totalDays <= 180) {
          // Últimos 6 meses
          intervals = this.getMonthsInRange(
            new Date(from * 1000),
            new Date(to * 1000)
          );
        } else if (totalDays <= 365) {
          // Último 1 ano agrupado em intervalos de 2 meses
          intervals = this.getBiMonthlyIntervals(
            new Date(from * 1000),
            new Date(to * 1000)
          );
        } else {
          // Intervalo maior que 1 ano agrupado por ano
          intervals = this.getYearlyIntervals(
            new Date(from * 1000),
            new Date(to * 1000)
          );
        }

        // Executa as requisições para cada intervalo de tempo
        const promises = intervals.map(interval =>
          this.fetchSummaryAccountDataForInterval(interval)
        );

        const results = await Promise.all(promises);

        // Processa os dados para o gráfico
        this.chartData = {
          monthly_data: results.map((data, index) => ({
            month: this.formatIntervalName(intervals[index], totalDays),
            data: this.processIntervalData(data),
          })),
        };
      } catch (error) {
        this.errorInReq = true;
      }
    },

    // Busca dados do servidor para um intervalo específico
    async fetchSummaryAccountDataForInterval(interval) {
      const params = this.formatDateParams(interval);
      const payload = this.getRequestPayloadFormatted(params);

      if (this.inbox.id) {
        // Se uma caixa de entrada estiver especificada, inclua o ID da caixa de entrada no payload
        payload.inbox_id = this.inbox.id;
      }

      try {
        const data = await this.$store.dispatch(
          'fetchConversationResolutionReasons',
          payload
        );

        return data;
      } catch (error) {
        this.errorInReq = true;
        return null;
      }
    },

    // Gera um array de intervalos diários entre duas datas
    getDaysInRange(from, to) {
      const days = [];
      let current = from;

      while (current <= to) {
        const startOfDay = current;
        const endOfDay = current + 24 * 60 * 60;
        days.push({
          startDate: startOfDay,
          endDate: Math.min(endOfDay, to + 24 * 60 * 60 - 1),
        });
        current += 24 * 60 * 60;
      }

      return days;
    },

    // Gera um array de intervalos mensais entre duas datas
    getMonthsInRange(startDate, endDate) {
      const months = [];
      let current = new Date(startDate);

      while (current <= endDate) {
        const startOfMonth = new Date(
          current.getFullYear(),
          current.getMonth(),
          1
        );
        const endOfMonth = new Date(
          current.getFullYear(),
          current.getMonth() + 1,
          0
        );
        months.push({
          startDate: Math.max(
            startOfMonth.getTime() / 1000,
            startDate.getTime() / 1000
          ),
          endDate: Math.min(
            endOfMonth.getTime() / 1000,
            endDate.getTime() / 1000
          ),
        });
        current.setMonth(current.getMonth() + 1);
      }

      return months;
    },

    // Função modificada para gerar intervalos de 2 meses
    getBiMonthlyIntervals(startDate, endDate) {
      const intervals = [];
      let current = new Date(startDate);

      while (current <= endDate) {
        const startOfInterval = new Date(
          current.getFullYear(),
          current.getMonth(),
          1
        );
        current.setMonth(current.getMonth() + 2);
        const endOfInterval = new Date(
          current.getFullYear(),
          current.getMonth(),
          0
        );
        intervals.push({
          startDate: Math.max(
            startOfInterval.getTime() / 1000,
            startDate.getTime() / 1000
          ),
          endDate: Math.min(
            endOfInterval.getTime() / 1000,
            endDate.getTime() / 1000
          ),
        });
      }

      return intervals;
    },

    // Gera um array de intervalos anuais entre duas datas
    getYearlyIntervals(startDate, endDate) {
      const years = [];
      let current = new Date(startDate);

      while (current <= endDate) {
        const startOfYear = new Date(current.getFullYear(), 0, 1);
        const endOfYear = new Date(current.getFullYear(), 11, 31);
        years.push({
          startDate: Math.max(
            startOfYear.getTime() / 1000,
            startDate.getTime() / 1000
          ),
          endDate: Math.min(
            endOfYear.getTime() / 1000,
            endDate.getTime() / 1000
          ),
        });
        current.setFullYear(current.getFullYear() + 1);
      }

      return years;
    },

    // Formata o nome do intervalo (mensal, bimestral ou anual) para exibição no gráfico
    formatIntervalName(interval, totalDays) {
      const { startDate, endDate } = interval;
      const intervalDays = Math.round((endDate - startDate) / (24 * 60 * 60));

      // Função auxiliar para formatar data
      const formatDate = (date, options) =>
        new Date(date * 1000).toLocaleDateString(
          this.currentLanguage(),
          options
        );

      // Constantes
      const WEEK_IN_DAYS = 7;
      const MONTH_IN_DAYS = 30;
      const YEAR_IN_DAYS = 365;

      if (totalDays > YEAR_IN_DAYS) {
        // Para intervalos maiores que 1 ano: exibe "ano"
        return formatDate(startDate, { year: 'numeric' });
      }

      if (intervalDays <= WEEK_IN_DAYS) {
        // Para intervalos diários: exibe "dia mês"
        return formatDate(startDate, { day: '2-digit', month: 'short' });
      }

      if (intervalDays <= MONTH_IN_DAYS) {
        // Para intervalos de até 30 dias: exibe apenas "mês"
        return formatDate(startDate, { month: 'short' });
      }

      if (intervalDays <= YEAR_IN_DAYS) {
        // Para intervalos de até 1 ano: exibe "Jan/Fev", "Mar/Abr", etc.
        const startMonth = formatDate(startDate, { month: 'short' });
        const endMonth = formatDate(endDate, { month: 'short' });
        return `${startMonth}/${endMonth}`;
      }

      // Caso não atenda a nenhuma condição acima, retorna uma string vazia
      return '';
    },
    // Formata as datas para o formato adequado para a requisição ao backend
    formatDateParams({ startDate, endDate }) {
      return {
        startDate: format(new Date(startDate * 1000), 'yyyy-MM-dd'),
        endDate: format(new Date(endDate * 1000), 'yyyy-MM-dd'),
      };
    },

    // Obtém o idioma atual do usuário para formatação de data
    currentLanguage() {
      let currentLanguage = this.$root.$i18n.locale.trim();
      if (currentLanguage === 'pt_BR') currentLanguage = 'pt-BR';
      else if (currentLanguage === 'es') currentLanguage = 'es-ES';
      else if (!['es', 'en'].includes(currentLanguage)) currentLanguage = 'en';
      return currentLanguage;
    },

    // Busca os dados de resumo da conta
    async fetchSummaryAccountData() {
      if (!this.dateRange || !this.dateRange.range) {
        throw new Error('Intervalo de data inválido');
      }

      const [from, to] = this.dateRange.range;
      const params = {
        startDate: format(new Date(from * 1000), 'yyyy-MM-dd'),
        endDate: format(new Date(to * 1000), 'yyyy-MM-dd'),
      };

      const payload = this.getRequestPayloadFormatted(params);
      if (this.inbox.id) {
        payload.inbox_id = this.inbox.id;
      }

      try {
        let data = await this.$store.dispatch(
          'fetchConversationResolutionReasons',
          payload
        );

        if (
          this.submetric &&
          this.submetric.type &&
          this.submetric.type !== '' &&
          !this.checksIfSeveralSubmetricsAreSelected
        ) {
          data = {
            ...data,
            inboxes_summary: data.inboxes_summary.map(inbox => {
              // Filtrando os resolutions_summary
              const filteredResolutions = inbox.resolutions_summary.filter(
                resolution => resolution.resolution_type === this.submetric.type
              );

              // Somando os counts dos itens filtrados por inbox
              const totalResolutionsForInbox = filteredResolutions.reduce(
                (sum, resolution) => sum + resolution.count,
                0
              );

              return {
                ...inbox,
                resolutions_summary: filteredResolutions,
                total_resolutions: totalResolutionsForInbox,
              };
            }),
          };
        }

        this.summaryData = data;
        return data;
      } catch (error) {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        this.errorInReq = true;
        return null;
      }
    },

    // Processa os dados retornados do backend para preparar para exibição no gráfico
    processIntervalData(data) {
      if (
        !data ||
        typeof data !== 'object' ||
        !data.inboxes_summary ||
        !Array.isArray(data.inboxes_summary)
      ) {
        return [];
      }

      // Cria um objeto para armazenar o total de resoluções por tipo
      const resolutionsByType = {};

      // Itera sobre cada inbox e suas resoluções
      data.inboxes_summary.forEach(inbox => {
        inbox.resolutions_summary.forEach(resolution => {
          const type = resolution.resolution_type;
          if (!resolutionsByType[type]) {
            resolutionsByType[type] = 0;
          }
          resolutionsByType[type] += resolution.count;
        });
      });

      return Object.entries(resolutionsByType).map(([type, count]) => ({
        type,
        count,
      }));
    },

    // Calcula o total de um tipo específico de resolução
    calculateTypeCount(data, type) {
      return data.inboxes_summary
        .flatMap(inbox => inbox.resolutions_summary)
        .filter(resolution => resolution.resolution_type === type)
        .reduce((sum, resolution) => sum + resolution.count, 0);
    },

    // Função para buscar dados dependendo do tipo de display selecionado
    async fetchData() {
      if (this.isSummaryDisplay) await this.fetchSummaryAccountData();
      if (this.isDonutDisplay) await this.fetchSummaryAccountData();
      if (this.isColumnChartDisplay) {
        await this.fetchBarData();
      }
      if (this.isTableDisplay) await this.fetchSummaryAccountData();
      this.isLoading = false;
    },

    // Exibe a pré-visualização do widget
    showPreview() {
      this.shouldShowPreview = true;
      this.fetchData();
    },

    // Formata o payload para a requisição ao servidor
    getRequestPayloadFormatted({ startDate, endDate }) {
      if (!startDate || !endDate) {
        throw new Error('Parâmetros de data inválidos');
      }

      return {
        start_date: startDate,
        end_date: endDate,
      };
    },
  },
};
</script>

<template>
  <div
    ref="widget"
    class="max-h-full h-full overflow-auto pt-3 px-3 widget-container"
  >
    <!-- Container principal do widget -->
    <div class="w-full flex relative">
      <div class="flex gap-1 max-w-[calc(100%-40px)] flex-wrap">
        <!-- NOME DA MÉTRICA SELECIONADA -->
        <div
          v-if="widgetVisualizationOptions.metric"
          class="widget_preferences_tags bg-slate-50 border-slate-50"
        >
          {{
            $t('CUSTOM_DASHBOARDS.WIDGET_TYPES.CONVERSATION_RESOLUTION_REASONS')
          }}
        </div>
        <!-- NAME OF SELECTED SUB METRIC -->
        <div
          v-if="widgetVisualizationOptions.submetric"
          class="widget_preferences_tags bg-woot-50 border-woot-50"
        >
          <span v-if="checksIfSeveralSubmetricsAreSelected">
            {{ selectedMultipleSubmetrics.map(item => item.title).join(' | ') }}
          </span>
          <!-- Caso contrário, exibe apenas uma submétrica -->
          <span v-else>
            {{ selectedSubmetric.title }}
          </span>
        </div>
        <!-- INTERVALO DE DATA SELECIONADO -->
        <div
          v-if="widgetVisualizationOptions.dateRange"
          class="widget_preferences_tags bg-woot-50 border-woot-50"
        >
          <span class="text-[black]">
            {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.INTERVAL') }}
          </span>
          &nbsp;{{ dateRangeDescription }}
        </div>
        <!-- NOME DA CAIXA DE ENTRADA (SE SELECIONADA) -->
        <div
          v-if="inbox.id && widgetVisualizationOptions.inbox"
          class="widget_preferences_tags bg-yellow-100 border-yellow-100"
        >
          <InboxName
            :inbox="currentInbox"
            :text-length="30"
            class="ltr:ml-0 rtl:mr-0 mb-0 m-0 mr-0 text-slate-800 text-xxs"
          />
        </div>
      </div>

      <!-- SEÇÃO DE BOTÕES DE EDIÇÃO/GRAVAÇÃO DO WIDGET -->
      <div v-if="canEdit" class="z-[999] absolute right-0">
        <!-- BOTÃO PARA ABRIR A SEÇÃO DE EDIÇÃO -->
        <woot-button
          class="flex items-center"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="settings"
          @click="openEditModal"
        />
      </div>
    </div>

    <!-- Conteúdo principal do widget -->
    <div
      class="w-full pb-3 flex justify-center items-center"
      :class="widgetContentHeightClass"
    >
      <!-- Adicionando botão de prévia para evitar requisições desnecessárias.
      A requisição é feita apenas quando o cliente exibe o display (não está editando/criando).
      Isso resolve o problema de múltiplas requisições desnecessárias ao modificar um único widget -->
      <woot-button v-if="canEdit && !shouldShowPreview" @click="showPreview">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.VIEW_PREVIEW') }}
      </woot-button>
      <div v-else>
        <!-- SPINNER AGUARDANDO O FIM DA REQUISIÇÃO -->
        <woot-loading-state
          v-if="isLoading"
          class="text-xxs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <!-- QUANDO A REQUISIÇÃO NÃO É BEM-SUCEDIDA -->
        <div v-else-if="errorInReq" class="text-red-600 text-xs">
          ⚠️ {{ $t('REPORT.DATA_FETCHING_FAILED') }}
        </div>
        <!-- DADOS SENDO EXIBIDOS -->
        <div v-else>
          <!-- Exibe o componente de resumo, caso esteja configurado para isso -->
          <SummaryIndicator
            v-if="isSummaryDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :content="summaryContent"
          />
          <!-- Exibe o componente de tabela, caso esteja configurado para isso -->
          <TableIndicator
            v-if="isTableDisplay"
            :id="id"
            :data="summaryData"
            :size-x="sizeX"
            :size-y="sizeY"
            :content="summaryContent"
            :display="display"
            :date-range="dateRange"
            :inbox="inbox"
          />
          <!-- Exibe o gráfico de rosca, caso esteja configurado para isso -->
          <DonutChart
            v-if="isDonutDisplay"
            :multiple-submetrics="multipleSubmetrics"
            :data="summaryData"
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
          />
          <!-- Exibe o gráfico de colunas, caso esteja configurado para isso -->
          <ColumnChart
            v-else-if="isColumnChartDisplay"
            group-by-inbox
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
            :date-range="dateRange"
            :show-detailed-msgs="
              widgetVisualizationOptions.showDetailedMsgsColChart
            "
            :data="chartData"
          />
        </div>
      </div>
    </div>
  </div>
</template>
